"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { MinusCircle, PlusCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { t } from "@/lib/localization";

interface TimestampInputProps {
    valueInSeconds: number;
    onValueChange: (value: number) => void;
    side?: "bottom" | "top" | "right" | "left" | undefined;
    align?: "center" | "end" | "start" | undefined;
    disabled?: boolean;
    triggerClassName?: string;
    showMs?: boolean;
}

type InputId = "hours" | "minutes" | "seconds" | "ms";

export default function TimestampInput({
    valueInSeconds,
    onValueChange,
    side,
    align,
    disabled,
    triggerClassName,
    showMs = false,
}: TimestampInputProps) {
    const secondsToHMSMs = (secs: number) => {
        const totalTenths = Math.round(secs * 10);
        const ms = totalTenths % 10;
        const wholeSecs = Math.floor(totalTenths / 10);
        const h = Math.floor(wholeSecs / 3600);
        const m = Math.floor((wholeSecs % 3600) / 60);
        const s = wholeSecs % 60;
        return { h, m, s, ms };
    };

    const initial = secondsToHMSMs(valueInSeconds);

    const [hours, setHours] = useState<string>(pad(initial.h));
    const [minutes, setMinutes] = useState<string>(pad(initial.m));
    const [seconds, setSeconds] = useState<string>(pad(initial.s));
    const [ms, setMs] = useState<string>(initial.ms.toString());
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const { h, m, s, ms } = secondsToHMSMs(valueInSeconds);
        setHours(pad(h));
        setMinutes(pad(m));
        setSeconds(pad(s));
        setMs(ms.toString());
    }, [valueInSeconds]);

    const clamp = (val: number, min: number, max?: number) => {
        if (isNaN(val)) return min;
        if (max !== undefined) return Math.min(Math.max(val, min), max);
        return Math.max(val, min);
    };

    function pad(num: number) {
        return num.toString().padStart(2, "0");
    }

    const updateValue = (
        newH: string,
        newM: string,
        newS: string,
        newMs: string
    ) => {
        const parsedH = clamp(parseInt(newH, 10) || 0, 0, 9);
        const parsedM = clamp(parseInt(newM, 10) || 0, 0, 59);
        const parsedS = clamp(parseInt(newS, 10) || 0, 0, 59);
        const parsedMs = showMs ? clamp(parseInt(newMs, 10) || 0, 0, 9) : 0;
        onValueChange(parsedH * 3600 + parsedM * 60 + parsedS + parsedMs / 10);
    };

    const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, "");
        const clampedVal = pad(clamp(parseInt(val, 10) || 0, 0, 9));
        setHours(clampedVal);
        updateValue(clampedVal, minutes, seconds, ms);
    };

    const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, "");
        const clampedVal = pad(clamp(parseInt(val, 10) || 0, 0, 59));
        setMinutes(clampedVal);
        updateValue(hours, clampedVal, seconds, ms);
    };

    const handleSecondsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, "");
        const clampedVal = pad(clamp(parseInt(val, 10) || 0, 0, 59));
        setSeconds(clampedVal);
        updateValue(hours, minutes, clampedVal, ms);
    };

    const handleMsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, "");
        const clampedVal = clamp(parseInt(val, 10) || 0, 0, 9).toString();
        setMs(clampedVal);
        updateValue(hours, minutes, seconds, clampedVal);
    };

    const fieldMax = (field: InputId) =>
        field === "hours" ? 9 : field === "ms" ? 9 : 59;

    const fieldValue = (field: InputId) =>
        field === "hours"
            ? hours
            : field === "minutes"
              ? minutes
              : field === "seconds"
                ? seconds
                : ms;

    const setField = (field: InputId, paddedVal: string) => {
        if (field === "hours") {
            setHours(paddedVal);
            updateValue(paddedVal, minutes, seconds, ms);
        } else if (field === "minutes") {
            setMinutes(paddedVal);
            updateValue(hours, paddedVal, seconds, ms);
        } else if (field === "seconds") {
            setSeconds(paddedVal);
            updateValue(hours, minutes, paddedVal, ms);
        } else {
            setMs(paddedVal);
            updateValue(hours, minutes, seconds, paddedVal);
        }
    };

    const increment = (field: InputId) => {
        const currentVal = parseInt(fieldValue(field), 10);
        const newVal = clamp(currentVal + 1, 0, fieldMax(field));
        setField(field, field === "ms" ? newVal.toString() : pad(newVal));
    };

    const decrement = (field: InputId) => {
        const currentVal = parseInt(fieldValue(field), 10);
        const newVal = clamp(currentVal - 1, 0);
        setField(field, field === "ms" ? newVal.toString() : pad(newVal));
    };

    const inputs: {
        label: { keyName: string };
        id: InputId;
        value: string;
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    }[] = [
        ...(showMs
            ? [
                  {
                      label: { keyName: "timestamp-input.popover.ms" },
                      id: "ms" as InputId,
                      value: ms,
                      onChange: handleMsChange,
                  },
              ]
            : []),
        {
            label: { keyName: "timestamp-input.popover.seconds" },
            id: "seconds" as InputId,
            value: seconds,
            onChange: handleSecondsChange,
        },
        {
            label: { keyName: "timestamp-input.popover.minutes" },
            id: "minutes" as InputId,
            value: minutes,
            onChange: handleMinutesChange,
        },
        {
            label: { keyName: "timestamp-input.popover.hours" },
            id: "hours" as InputId,
            value: hours,
            onChange: handleHoursChange,
        },
    ];

    return (
        <Popover
            open={open}
            onOpenChange={(open) => {
                if (disabled) {
                    setOpen(false);
                } else {
                    setOpen(open);
                }
            }}
        >
            <PopoverTrigger asChild>
                <div
                    className={cn(
                        "flex h-9 cursor-pointer w-full items-center rounded-md border border-input bg-background px-2.5 py-2 text-sm ring-offset-background",
                        disabled ? "cursor-default" : "cursor-pointer",
                        triggerClassName
                    )}
                >
                    <p>
                        {hours}:{minutes}:{seconds}
                        {showMs ? `.${ms}` : ""}
                    </p>
                </div>
            </PopoverTrigger>
            <PopoverContent
                side={side}
                align={align}
                className="flex flex-col gap-2 w-fit p-4"
            >
                {inputs.map((input) => (
                    <div
                        key={input.id}
                        className="flex items-center justify-between gap-8"
                    >
                        <Label htmlFor={input.id}>
                            {t(input.label.keyName, undefined, undefined)}
                            :
                        </Label>
                        <div className="flex items-center gap-x-1">
                            <MinusCircle
                                className="size-[18px] text-gray-400 hover:text-gray-600 transition cursor-pointer"
                                onClick={() => decrement(input.id)}
                            />
                            <Input
                                dir="ltr"
                                id={input.id}
                                type="text"
                                value={input.value}
                                onChange={input.onChange}
                                className="h-8 w-12 text-center"
                            />
                            <PlusCircle
                                className="size-[18px] text-gray-400 hover:text-gray-600 transition cursor-pointer"
                                onClick={() => increment(input.id)}
                            />
                        </div>
                    </div>
                ))}
            </PopoverContent>
        </Popover>
    );
}
